// fingerprintService.js
import baseURL from '../baseURL';

// Local SecuGen bridge on client PC (e.g. http://localhost:5001). Set REACT_APP_LOCAL_FINGERPRINT_BRIDGE_URL in .env
const LOCAL_BRIDGE_URL = (typeof process !== 'undefined' && process.env?.REACT_APP_LOCAL_FINGERPRINT_BRIDGE_URL) || 'http://localhost:5001';

class FingerprintService {
  constructor() {
    this.baseUrl = `${baseURL}/pledges`;
    this.isConnected = false;
  }

  async initialize() {
    try {
      const response = await fetch(`${this.baseUrl}/status`);
      this.isConnected = response.ok;
      return this.isConnected;
    } catch (error) {
      console.error('Error connecting to fingerprint service:', error);
      this.isConnected = false;
      return false;
    }
  }

  /** Capture on client PC via local bridge only. No fallback to backend. */
  async capture() {
    try {
      const localRes = await fetch(`${LOCAL_BRIDGE_URL}/capture`, { method: 'POST' });
      if (!localRes.ok) {
        throw new Error(
          'Start the fingerprint bridge on this PC: run run-secugen-http-server.bat from C:\\girvi\\secugen-bridge'
        );
      }
      const localData = await localRes.json().catch(() => ({}));
      const payload = localData?.data ?? localData;
      if (!payload?.template) {
        throw new Error(
          'Start the fingerprint bridge on this PC: run run-secugen-http-server.bat from C:\\girvi\\secugen-bridge'
        );
      }
      return {
        image: payload.image ?? null,
        template: payload.template,
        quality: Number(payload.quality ?? 0)
      };
    } catch (error) {
      if (error.message && error.message.includes('run-secugen-http-server')) {
        throw error;
      }
      console.error('Error capturing fingerprint:', error);
      throw new Error(
        'Could not reach the fingerprint bridge. Start run-secugen-http-server.bat on this PC (C:\\girvi\\secugen-bridge) and try again.'
      );
    }
  }

  async match(template1, template2, threshold = 60) {
    try {
      const response = await fetch(`${this.baseUrl}/match-fingerprint`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ template1, template2, threshold })
      });
      
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.message || 'Failed to match fingerprints');
      }

      return data?.data || data;
    } catch (error) {
      console.error('Error matching fingerprints:', error);
      return { matched: false, score: 0 };
    }
  }

  async verifyWithPledge(pledgeId, template) {
    try {
      const response = await fetch(`${this.baseUrl}/${pledgeId}/verify-fingerprint`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ template })
      });
      
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.message || 'Failed to verify fingerprint');
      }

      return data?.data || data;
    } catch (error) {
      console.error('Error verifying fingerprint:', error);
      return { verified: false, score: 0 };
    }
  }

  /** Capture on client PC (local bridge), then search on VPS with template. */
  async searchPledgesByScan(threshold = 60) {
    try {
      const captured = await this.capture();
      const template = captured?.template;
      if (!template) {
        throw new Error('No fingerprint. Start the local SecuGen bridge on this PC (e.g. http://localhost:5001).');
      }
      const response = await fetch(`${this.baseUrl}/search-by-fingerprint`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ template, threshold })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.message || 'Failed to search pledges by fingerprint');
      }

      return data?.data || data;
    } catch (error) {
      console.error('Error searching pledges by fingerprint:', error);
      throw new Error(error.message || 'Failed to search pledges by fingerprint');
    }
  }

  disconnect() {
    this.isConnected = false;
    // Add any cleanup code if needed
  }
}

// Export a singleton instance
export const fingerprintService = new FingerprintService();